import React from "react";
import { ArrowRight, Sparkles, Compass } from "lucide-react";
import { COMPANY, HERO_STATS, MARQUEE_KEYWORDS } from "../../data/constants";
import BrandWordmark from "../BrandWordmark";

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY - 88;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

export default function HeroSection() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative isolate overflow-hidden border-b border-white/10 bg-[#0B0F19]"
    >
      {/* Background photo */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/7f72252b-c138-4e30-9fe4-a617a30a0335/images/01c984057bc44fcc454bd2be18e4dc0eb601972131473965ff90137c40729a3c.png"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19]/95 via-[#0B0F19]/85 to-[#0B0F19]" />
        <div className="absolute inset-0 stripe-bg opacity-50" />
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 pt-16 md:pt-24 pb-0">
        {/* Tag row */}
        <div className="flex items-center gap-3 mb-10 md:mb-14">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#F47B20]/40 text-[#F47B20] text-[10px] font-bold uppercase tracking-[0.3em]">
            <Sparkles className="w-3 h-3" /> Since {COMPANY.established}
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 text-white/70 text-[10px] font-bold uppercase tracking-[0.3em]">
            <Compass className="w-3 h-3" /> Pune · India
          </span>
        </div>

        {/* Headline + stats bento */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left: headline */}
          <div className="lg:col-span-7">
            <h1
              data-testid="hero-headline"
              className="font-display font-black uppercase tracking-tighter text-white text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[0.92]"
            >
              <span className="block">Consult.</span>
              <span className="block">
                Automate.
              </span>
              <span className="block text-[#F47B20]">Grow.</span>
            </h1>

            <p
              data-testid="hero-sub"
              className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
            >
              {COMPANY.tagline} We turn workflow chaos into measurable growth — from
              NetSuite & Odoo rollouts to Celigo integrations, websites and
              brand-aware print.
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#services"
                onClick={scrollTo("services")}
                data-testid="hero-cta-services"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#F47B20] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#FF8A33] transition-all duration-300 hover:-translate-y-0.5"
              >
                Explore Services
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#contact"
                onClick={scrollTo("contact")}
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-transparent border border-white/25 text-white font-bold uppercase tracking-[0.2em] text-xs hover:border-[#F47B20] hover:text-[#F47B20] transition-all duration-300 hover:-translate-y-0.5"
              >
                Request a Quote
              </a>
            </div>
          </div>

          {/* Right: wordmark + stats */}
          <div className="lg:col-span-5 lg:pl-6">
            <div className="border border-white/10 bg-white/[0.02] p-6 md:p-8">
              <BrandWordmark size="md" align="left" data-testid="hero-wordmark" />
              <div className="grid grid-cols-2 mt-8 border-t border-l border-white/10">
                {HERO_STATS.map((s, idx) => (
                  <div
                    key={idx}
                    data-testid={`hero-stat-${idx}`}
                    className="border-r border-b border-white/10 p-5 md:p-6"
                  >
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F47B20]">
                      {s.kpi}
                    </p>
                    <p className="mt-3 font-condensed text-3xl md:text-4xl text-white leading-none">
                      {s.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-16 md:mt-24 border-t border-b border-white/10 py-6 bg-black/30">
        <div className="marquee">
          <div className="marquee__track font-condensed text-3xl md:text-5xl">
            {[0, 1].map((dup) => (
              <div className="marquee__item" key={dup} aria-hidden={dup === 1}>
                {MARQUEE_KEYWORDS.map((kw, i) => (
                  <React.Fragment key={`${dup}-${i}`}>
                    <span className={i % 2 === 0 ? "text-white" : "text-[#F47B20]"}>
                      {kw}
                    </span>
                    <span className="text-white/40">/</span>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
